import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../api/queryKeys';
import { buffetService } from '../api/services/buffetService';
import type {
  BuffetBillRequest,
  BuffetBillResultDto,
  BuffetBoardDto,
  BuffetMarkRequest,
  BuffetTodayDto,
} from '../api/types/buffet.types';

/** GET /buffet/today — the outlet's buffet meals today. Read by the shell (tab) and the screen. */
export function useBuffetToday(enabled: boolean) {
  return useQuery<BuffetTodayDto, Error>({
    queryKey: queryKeys.buffetToday(),
    queryFn: () => buffetService.today(),
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/**
 * GET /buffet/{meal} — the board. Polled every 20 s while open, so a mark made at
 * another outlet (or by another waiter) shows without a tap.
 */
export function useBuffetBoard(meal: string | null) {
  return useQuery<BuffetBoardDto, Error>({
    queryKey: queryKeys.buffetBoard(meal ?? ''),
    queryFn: () => buffetService.board(meal!),
    enabled: !!meal,
    refetchInterval: 20_000,
    retry: 1,
  });
}

export interface BuffetMutations {
  mark: (meal: string, body: BuffetMarkRequest) => Promise<BuffetBoardDto>;
  undo: (meal: string, markId: string, reason: string) => Promise<BuffetBoardDto>;
  close: (meal: string, notes?: string) => Promise<BuffetBoardDto>;
  /** Slice 3: raises the bill (and charges the room when asked); the board in the answer replaces the cached one. */
  bill: (meal: string, body: BuffetBillRequest) => Promise<BuffetBillResultDto>;
}

/** Each answer is the new board: it replaces the cached one, and today's list refreshes. */
export function useBuffetMutations(): BuffetMutations {
  const qc = useQueryClient();
  return useMemo<BuffetMutations>(() => {
    const store = (meal: string, board: BuffetBoardDto) => {
      qc.setQueryData(queryKeys.buffetBoard(meal), board);
      void qc.invalidateQueries({ queryKey: queryKeys.buffetToday() });
      return board;
    };
    return {
      mark: async (meal, body) => store(meal, await buffetService.mark(meal, body)),
      undo: async (meal, markId, reason) => store(meal, await buffetService.undo(markId, reason)),
      close: async (meal, notes) => store(meal, await buffetService.close(meal, notes)),
      bill: async (meal, body) => {
        const result = await buffetService.bill(meal, body);
        if (result.board) store(meal, result.board);
        else void qc.invalidateQueries({ queryKey: queryKeys.buffetBoard(meal) });
        return result;
      },
    };
  }, [qc]);
}
